import { __assign } from 'tslib';
import { createLogger } from '../../utils/logger.js';
import { deepClone } from '../../utils/deepClone.js';

/**
 * Implements a Least Recently Used (LRU) cache for publisher configurations.
 *
 * This cache:
 * - Stores publisher configurations with access timestamps
 * - Automatically prunes least recently used entries when size limit is reached
 * - Tracks cache performance metrics (hits/misses)
 * - Protects default configuration from pruning
 * - Provides immutable configuration access
 *
 * @example
 * const cache = new ConfigurationCache(
 *   CACHE_CONSTANTS,
 *   defaultConfig,
 *   customLogger
 * );
 *
 * // Store configuration
 * cache.set('example.com', publisherConfig);
 *
 * // Retrieve configuration
 * const config = cache.get('example.com');
 *
 * // Monitor cache performance
 * const metrics = cache.getMetrics();
 *
 * @remarks
 * - Cache size is configurable via CACHE_CONSTANTS
 * - Default configuration is always preserved
 * - Uses structuredClone (via the deepClone utility) for immutable storage but
 *   falls back to JSON.stringify/parse if structuredClone is unavailable
 * - Automatically logs metrics on cache misses
 * - Warns when approaching size limits
 *
 * @see {@link Publisher} for stored configuration type
 * @see {@link CACHE_CONSTANTS} for cache configuration options
 */
var ConfigurationCache = /** @class */ (function () {
    function ConfigurationCache(constants, defaultConfig, logger) {
        this.constants = constants;
        this.defaultConfig = defaultConfig;
        this.metrics = {
            hits: 0,
            misses: 0,
        };
        this.cache = new Map();
        this.set('default', defaultConfig);
        this.logger =
            logger ||
                createLogger({
                    name: 'ConfigurationCache',
                    logLevel: constants.DEFAULT_LOG_LEVEL,
                });
    }
    /**
     * Logs the current cache metrics using the configured logger.
     * This method retrieves the cache metrics and outputs them
     * at the debug log level for monitoring and debugging purposes.
     *
     * @private
     */
    ConfigurationCache.prototype.logMetrics = function () {
        this.logger.debug('Cache metrics', this.getMetrics());
    };
    /**
     * Updates the last accessed time for a specific cache entry.
     *
     * @param key - The key of the cache entry to update.
     * If the entry exists in the cache, its `lastAccessed` property
     * will be set to the current timestamp.
     *
     * @private
     */
    ConfigurationCache.prototype.updateAccessTime = function (key) {
        var entry = this.cache.get(key);
        if (entry) {
            entry.lastAccessed = Date.now();
        }
    };
    /**
     * Removes the least recently accessed entries from the cache to ensure
     * the cache size does not exceed the maximum allowed size (`MAX_SIZE`).
     *
     * Entries are sorted by their `lastAccessed` timestamp in ascending order,
     * and the oldest entries are removed until the cache size is within the limit.
     *
     * The entry with the key `'default'` is never removed, regardless of its
     * access time.
     *
     * @private
     */
    ConfigurationCache.prototype.prune = function () {
        if (this.cache.size <= this.constants.MAX_SIZE)
            return;
        var entries = Array.from(this.cache.entries());
        var toRemove = entries
            .sort(function (_a, _b) {
            var a = _a[1];
            var b = _b[1];
            return a.lastAccessed - b.lastAccessed;
        })
            .slice(0, entries.length - this.constants.MAX_SIZE);
        for (var _i = 0, toRemove_1 = toRemove; _i < toRemove_1.length; _i++) {
            var key = toRemove_1[_i][0];
            if (key !== 'default') {
                this.cache.delete(key);
            }
        }
    };
    /**
     * Retrieves a publisher configuration from the cache using the specified key.
     * If the key is not found in the cache, increments the cache miss metric,
     * logs the metrics, and returns the default configuration.
     * If the key is found, increments the cache hit metric, updates the access time,
     * and returns the associated configuration.
     *
     * @param key - The key used to look up the publisher configuration in the cache.
     * @returns The publisher configuration associated with the key, or the default configuration if the key is not found.
     */
    ConfigurationCache.prototype.get = function (key) {
        var entry = this.cache.get(key);
        if (!entry) {
            this.metrics.misses++;
            this.logMetrics();
            // Only return default if specifically requested
            return key === 'default' ? this.defaultConfig : undefined;
        }
        this.metrics.hits++;
        this.updateAccessTime(key);
        return entry.config;
    };
    /**
     * Stores a publisher configuration in the cache with the specified key.
     * If the cache size exceeds a warning threshold, a warning is logged to the console.
     * The configuration is stored as a deep clone to prevent external modifications.
     * After adding the new entry, the cache is pruned to ensure it adheres to size constraints.
     *
     * @param key - The unique identifier for the configuration to be cached.
     * @param config - The publisher configuration object to be stored in the cache.
     */
    ConfigurationCache.prototype.set = function (key, config) {
        if (this.cache.size >=
            this.constants.MAX_SIZE * this.constants.WARN_THRESHOLD) {
            console.warn("Cache size (".concat(this.cache.size, ") approaching maximum (").concat(this.constants.MAX_SIZE, ")"));
        }
        this.cache.set(key, {
            config: deepClone(config),
            lastAccessed: Date.now(),
        });
        this.prune();
    };
    /**
     * Retrieves the current metrics of the configuration cache.
     *
     * @returns An object containing the following metrics:
     * - `size`: The current size of the cache.
     * - `hitRate`: The ratio of cache hits to total cache accesses (hits + misses).
     * - `maxSize`: The maximum allowed size of the cache.
     * - `warnThreshold`: The threshold at which warnings are triggered for cache usage.
     * - Other properties spread from the `metrics` object.
     */
    ConfigurationCache.prototype.getMetrics = function () {
        return __assign(__assign({}, this.metrics), { size: this.cache.size, hitRate: this.metrics.hits / (this.metrics.hits + this.metrics.misses), maxSize: this.constants.MAX_SIZE, warnThreshold: this.constants.WARN_THRESHOLD });
    };
    return ConfigurationCache;
}());

export { ConfigurationCache as default };
